import React, { useReducer, useMemo, createContext, useEffect } from 'react';
import {
  createAnnotationMutation,
  updateAnnotationMutation,
  deleteAnnotationMutation,
} from '../queries/learnosityAssignmentAnnotations';
import log from '../utility/log';

export const initialState = {
  annotations: [],
  activeId: null,
  isNotesColumnExpanded: true,
};

export const TYPES = {
  CREATE_ANNOTATION: 'CREATE_ANNOTATION',
  READ_ANNOTATIONS: 'READ_ANNOTATIONS',
  UPDATE_ANNOTATION: 'UPDATE_ANNOTATION',
  DELETE_ANNOTATION: 'DELETE_ANNOTATION',
  SET_ACTIVE_ID: 'SET_ACTIVE_ID',
  TOGGLE_NOTES_COLUMN: 'TOGGLE_NOTES_COLUMN',
};

export const bluebookAnnotationsReducer = (state, action) => {
  switch (action.type) {
    case TYPES.CREATE_ANNOTATION:
      return {
        ...state,
        annotations: [...state.annotations, action.payload],
      };
    case TYPES.READ_ANNOTATIONS:
      return {
        ...state,
        annotations: action.payload,
      };
    case TYPES.UPDATE_ANNOTATION:
      return {
        ...state,
        annotations: state.annotations.map((annotation) =>
          annotation.id === action.payload.id
            ? { ...annotation, ...action.payload }
            : annotation,
        ),
      };
    case TYPES.DELETE_ANNOTATION:
      return {
        ...state,
        annotations: state.annotations.filter(
          (annotation) => annotation.id !== action.payload.id,
        ),
      };
    case TYPES.SET_ACTIVE_ID:
      return {
        ...state,
        activeId: action.payload,
      };
    case TYPES.TOGGLE_NOTES_COLUMN:
      return {
        ...state,
        isNotesColumnExpanded: !state.isNotesColumnExpanded,
      };
    default:
      return state;
  }
};

export const BluebookAnnotationsContext = createContext();

export const BluebookAnnotationsProvider = ({
  initState = {},
  children,
  fetchAnnotations,
  createAnnotation,
  updateAnnotation,
  deleteAnnotation,
}) => {
  const [state, dispatch] = useReducer(bluebookAnnotationsReducer, {
    ...initialState,
    ...initState,
  });

  useEffect(() => {
    if (fetchAnnotations && fetchAnnotations.learnosityAssignmentAnnotations) {
      dispatch({
        type: TYPES.READ_ANNOTATIONS,
        payload: fetchAnnotations.learnosityAssignmentAnnotations,
      });
    }
  }, [fetchAnnotations]);

  const addAnnotation = async (createAnnotationData) => {
    const { annotation, assignmentId, highlight } = createAnnotationData;

    const existingAnnotation = state.annotations.find(
      (ann) =>
        ann.highlight.item === highlight.item &&
        ann.highlight.offset === highlight.offset &&
        ann.highlight.path === highlight.path &&
        ann.highlight.selection === highlight.selection &&
        ann.highlight.color === highlight.color,
    );
    if (existingAnnotation) {
      dispatch({
        type: TYPES.SET_ACTIVE_ID,
        payload: existingAnnotation.id,
      });
    } else {
      try {
        const response = await createAnnotation({
          variables: {
            annotation,
            assignmentId: parseInt(assignmentId, 10),
            highlight,
          },
        });
        const responseStatusOk = response.data.createLearnosityAnnotation.ok;
        if (responseStatusOk) {
          const { data: refetchData } = await fetchAnnotations.refetch({
            assignmentId: parseInt(assignmentId, 10),
          });
          dispatch({
            type: TYPES.READ_ANNOTATIONS,
            payload: refetchData.learnosityAssignmentAnnotations,
          });
          const newAnnotation =
            refetchData.learnosityAssignmentAnnotations.find(
              (ann) =>
                ann.highlight.item === highlight.item &&
                ann.highlight.offset === highlight.offset &&
                ann.highlight.path === highlight.path &&
                ann.highlight.selection === highlight.selection &&
                ann.highlight.color === highlight.color,
            );
          if (newAnnotation) {
            dispatch({
              type: TYPES.SET_ACTIVE_ID,
              payload: newAnnotation.id,
            });
          }
        } else {
          log.error('Failed to create annotation');
        }
      } catch (error) {
        log.error('Error creating annotation', error);
      }
    }
  };
  const editAnnotation = async (updateAnnotationData) => {
    const { id, annotation } = updateAnnotationData;

    try {
      const response = await updateAnnotation({
        variables: {
          learnosityAnnotationId: id,
          annotation,
        },
      });
      const responseStatusOk = response.data.updateLearnosityAnnotation.ok;
      if (responseStatusOk) {
        dispatch({
          type: TYPES.UPDATE_ANNOTATION,
          payload: { id, annotation },
        });
      } else {
        log.error('Failed to update annotation');
      }
    } catch (error) {
      log.error('Error updating annotation', error);
    }
  };
  const setActiveId = (id) => {
    dispatch({
      type: TYPES.SET_ACTIVE_ID,
      payload: id,
    });
  };

  const removeAnnotation = async (learnosityAnnotationId) => {
    try {
      const response = await deleteAnnotation({
        variables: {
          learnosityAnnotationId,
        },
      });
      const responseStatusOk = response.data.deleteLearnosityAnnotation.ok;
      if (responseStatusOk) {
        dispatch({
          type: TYPES.DELETE_ANNOTATION,
          payload: { id: learnosityAnnotationId },
        });
      } else {
        log.error('Failed to delete annotation');
      }
    } catch (error) {
      log.error('Error deleting annotation', error);
    }
  };
  const annotationManager = {
    addAnnotation,
    editAnnotation,
    setActiveId,
    removeAnnotation,
  };

  const store = useMemo(
    () => ({ state, dispatch, annotationManager }),
    [
      state,
      createAnnotationMutation,
      updateAnnotationMutation,
      deleteAnnotationMutation,
    ],
  );

  return (
    <BluebookAnnotationsContext.Provider value={store}>
      {children}
    </BluebookAnnotationsContext.Provider>
  );
};
